/**
 * PRD (Product Requirements Document) 解析器
 * 从需求文档自动生成开发任务
 * 
 * 灵感来自 claude-task-master 的 PRD 解析功能
 */

import { logger } from '../utils/logger';
import { Task, TaskPriority } from '../types';
import { v4 as uuidv4 } from 'uuid';

export interface ParsedRequirement {
  title: string;
  description: string;
  type: 'feature' | 'bug' | 'improvement' | 'task';
  priority: TaskPriority;
  complexity?: number;
  dependencies?: string[];
  acceptanceCriteria?: string[];
  technicalNotes?: string;
}

export interface PRDParseResult {
  title: string;
  overview: string;
  requirements: ParsedRequirement[];
  tasks: Task[];
  metadata: {
    totalTasks: number;
    estimatedHours: number;
    complexity: {
      high: number;
      medium: number;
      low: number;
    };
  };
}

export class PRDParser {
  private patterns = {
    // 识别需求部分
    requirementSection: /^#{1,3}\s*(需求|要求|功能|Features?|Requirements?|用户故事|User Stor(?:y|ies))/mi,
    // 识别单个需求项
    requirementItem: /^[-*]\s*(.+)/gm,
    // 识别优先级标记
    priority: /\[(高|中|低|high|medium|low|P[0-3])\]/i,
    // 识别任务类型
    taskType: /\[(功能|修复|优化|任务|feature|bugfix|improvement|task)\]/i,
    // 识别复杂度
    complexity: /复杂度[:：]\s*(\d+)|complexity[:：]\s*(\d+)/i,
    // 识别依赖
    dependency: /依赖[:：]\s*(.+)|depends?\s+on[:：]\s*(.+)/i,
    // 识别验收标准
    acceptanceCriteria: /^AC[:：]|验收标准[:：]/mi,
  };

  /**
   * 解析 PRD 文档
   */
  async parsePRD(content: string): Promise<PRDParseResult> {
    logger.info('Parsing PRD document');
    
    // 提取文档标题和概述
    const title = this.extractTitle(content);
    const overview = this.extractOverview(content);
    
    // 解析需求
    const requirements = this.extractRequirements(content);
    
    // 生成任务
    const tasks = this.generateTasksFromRequirements(requirements);
    
    // 计算元数据
    const metadata = this.calculateMetadata(tasks);
    
    return {
      title,
      overview,
      requirements,
      tasks,
      metadata
    };
  }

  /**
   * 提取文档标题
   */
  private extractTitle(content: string): string {
    const titleMatch = content.match(/^#\s+(.+)/m);
    return titleMatch ? titleMatch[1].trim() : 'Untitled PRD';
  }

  /**
   * 提取概述部分
   */
  private extractOverview(content: string): string {
    // 查找概述部分
    const overviewMatch = content.match(
      /^#{1,3}\s*(概述|简介|背景|Overview|Introduction|Background)\s*\n([\s\S]+?)(?=^#{1,3}\s|$)/mi
    );
    
    if (overviewMatch) {
      return overviewMatch[2].trim();
    }
    
    // 如果没有明确的概述部分，取第一段非标题文本
    const firstParagraph = content.match(/^#[^#\n]+\n+([^#\n]+)/m);
    return firstParagraph ? firstParagraph[1].trim() : '';
  }

  /**
   * 提取需求列表
   */
  private extractRequirements(content: string): ParsedRequirement[] {
    const requirements: ParsedRequirement[] = [];
    
    // 查找需求部分
    const sections = this.findRequirementSections(content);
    
    for (const section of sections) {
      const sectionRequirements = this.parseRequirementSection(section);
      requirements.push(...sectionRequirements);
    }
    
    // 如果没有找到明确的需求部分，尝试解析整个文档
    if (requirements.length === 0) {
      const fallbackRequirements = this.parseFallbackRequirements(content);
      requirements.push(...fallbackRequirements);
    }
    
    return requirements;
  }

  /**
   * 查找需求部分
   */
  private findRequirementSections(content: string): string[] {
    const sections: string[] = [];
    const lines = content.split('\n');
    let currentSection = '';
    let inRequirementSection = false;
    
    for (const line of lines) {
      if (this.patterns.requirementSection.test(line)) {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = line + '\n';
        inRequirementSection = true;
      } else if (inRequirementSection) {
        if (line.match(/^#{1,3}\s/) && !this.patterns.requirementSection.test(line)) {
          sections.push(currentSection);
          currentSection = '';
          inRequirementSection = false;
        } else {
          currentSection += line + '\n';
        }
      }
    }
    
    if (currentSection) {
      sections.push(currentSection);
    }
    
    return sections;
  }

  /**
   * 解析需求部分
   */
  private parseRequirementSection(section: string): ParsedRequirement[] {
    const requirements: ParsedRequirement[] = [];
    const items = section.match(this.patterns.requirementItem) || [];
    
    for (const item of items) {
      const requirement = this.parseRequirementItem(item);
      if (requirement) {
        requirements.push(requirement);
      }
    }
    
    return requirements;
  }

  /**
   * 解析单个需求项
   */
  private parseRequirementItem(item: string): ParsedRequirement | null {
    const cleanedItem = item.replace(/^[-*]\s*/, '').trim();
    if (!cleanedItem) return null;
    
    // 提取优先级
    const priorityMatch = cleanedItem.match(this.patterns.priority);
    const priority = this.parsePriority(priorityMatch?.[1] || 'medium');
    
    // 提取任务类型
    const typeMatch = cleanedItem.match(this.patterns.taskType);
    const type = this.parseTaskType(typeMatch?.[1] || 'feature');
    
    // 提取复杂度
    const complexityMatch = cleanedItem.match(this.patterns.complexity);
    const complexity = complexityMatch ? 
      parseInt(complexityMatch[1] || complexityMatch[2]) : 
      this.estimateComplexity(cleanedItem);
    
    // 清理标记后的标题
    let title = cleanedItem
      .replace(this.patterns.priority, '')
      .replace(this.patterns.taskType, '')
      .replace(this.patterns.complexity, '')
      .replace(/\[.*?\]/g, '') // 移除所有方括号标记
      .trim();
    
    // 分离标题和描述
    const [mainTitle, ...descParts] = title.split(/[:：]/);
    const description = descParts.join(':').trim() || mainTitle;
    
    return {
      title: mainTitle.trim(),
      description,
      type,
      priority,
      complexity,
      dependencies: [],
      acceptanceCriteria: []
    };
  }

  /**
   * 解析优先级
   */
  private parsePriority(priorityStr: string): TaskPriority {
    const normalized = priorityStr.toLowerCase();
    if (normalized === '高' || normalized === 'high' || normalized === 'p0') {
      return 'high';
    } else if (normalized === '低' || normalized === 'low' || normalized === 'p2' || normalized === 'p3') {
      return 'low';
    }
    return 'medium';
  }

  /**
   * 解析任务类型
   */
  private parseTaskType(typeStr: string): ParsedRequirement['type'] {
    const normalized = typeStr.toLowerCase();
    if (normalized === '修复' || normalized === 'bugfix' || normalized === 'fix') {
      return 'bug';
    } else if (normalized === '优化' || normalized === 'improvement' || normalized === 'enhance') {
      return 'improvement';
    } else if (normalized === '任务' || normalized === 'task') {
      return 'task';
    }
    return 'feature';
  }

  /**
   * 估算复杂度（1-10）
   */
  private estimateComplexity(description: string): number {
    let complexity = 5; // 基础复杂度
    
    // 根据描述长度调整
    if (description.length > 200) complexity += 1;
    if (description.length > 500) complexity += 1;
    
    // 根据关键词调整
    const complexKeywords = [
      '复杂', '集成', '重构', '架构', '优化', '性能', '安全',
      'complex', 'integrate', 'refactor', 'architecture', 'optimize', 'performance', 'security'
    ];
    
    const simpleKeywords = [
      '简单', '修复', '更新', '调整', '小',
      'simple', 'fix', 'update', 'adjust', 'minor'
    ];
    
    for (const keyword of complexKeywords) {
      if (description.toLowerCase().includes(keyword)) {
        complexity += 1;
      }
    }
    
    for (const keyword of simpleKeywords) {
      if (description.toLowerCase().includes(keyword)) {
        complexity -= 1;
      }
    }
    
    // 确保在有效范围内
    return Math.max(1, Math.min(10, complexity));
  }

  /**
   * 降级解析（当没有找到明确的需求部分时）
   */
  private parseFallbackRequirements(content: string): ParsedRequirement[] {
    const requirements: ParsedRequirement[] = [];
    const lines = content.split('\n');
    
    // 查找所有列表项
    for (const line of lines) {
      if (this.patterns.requirementItem.test(line)) {
        const requirement = this.parseRequirementItem(line);
        if (requirement) {
          requirements.push(requirement);
        }
      }
    }
    
    // 如果还是没有找到，尝试从标题生成
    if (requirements.length === 0) {
      const headings = content.match(/^#{2,3}\s+(.+)/gm) || [];
      for (const heading of headings) {
        const title = heading.replace(/^#{2,3}\s+/, '').trim();
        if (title && !this.patterns.requirementSection.test(title)) {
          requirements.push({
            title,
            description: title,
            type: 'feature',
            priority: 'medium',
            complexity: 5
          });
        }
      }
    }
    
    return requirements;
  }

  /**
   * 从需求生成任务
   */
  private generateTasksFromRequirements(requirements: ParsedRequirement[]): Task[] {
    const tasks: Task[] = [];
    
    for (let i = 0; i < requirements.length; i++) {
      const req = requirements[i];
      const task: Task = {
        id: uuidv4(),
        content: `${req.title}: ${req.description}`,
        priority: req.priority,
        status: 'pending',
        createdAt: new Date(),
        position: i,
        metadata: {
          type: req.type,
          complexity: req.complexity,
          source: 'prd'
        }
      };
      
      // 如果复杂度高，生成子任务
      if (req.complexity && req.complexity >= 8) {
        const subtasks = this.generateSubtasks(req);
        tasks.push(task, ...subtasks);
      } else {
        tasks.push(task);
      }
    }
    
    return tasks;
  }

  /**
   * 生成子任务
   */
  private generateSubtasks(requirement: ParsedRequirement): Task[] {
    const subtasks: Task[] = [];
    const baseId = uuidv4();
    
    // 根据任务类型生成不同的子任务
    if (requirement.type === 'feature') {
      subtasks.push(
        this.createSubtask(`设计 ${requirement.title} 的架构`, requirement.priority, 0),
        this.createSubtask(`实现 ${requirement.title} 的核心功能`, requirement.priority, 1),
        this.createSubtask(`为 ${requirement.title} 编写单元测试`, 'medium', 2),
        this.createSubtask(`编写 ${requirement.title} 的文档`, 'low', 3)
      );
    } else if (requirement.type === 'bug') {
      subtasks.push(
        this.createSubtask(`复现和定位 ${requirement.title}`, requirement.priority, 0),
        this.createSubtask(`修复 ${requirement.title}`, requirement.priority, 1),
        this.createSubtask(`验证 ${requirement.title} 的修复`, 'medium', 2)
      );
    }
    
    return subtasks;
  }

  /**
   * 创建子任务
   */
  private createSubtask(content: string, priority: TaskPriority, position: number): Task {
    return {
      id: uuidv4(),
      content,
      priority,
      status: 'pending',
      createdAt: new Date(),
      position,
      metadata: {
        isSubtask: true,
        source: 'prd-generated'
      }
    };
  }

  /**
   * 计算元数据
   */
  private calculateMetadata(tasks: Task[]): PRDParseResult['metadata'] {
    const complexityCount = { high: 0, medium: 0, low: 0 };
    let totalHours = 0;
    
    for (const task of tasks) {
      // 根据复杂度估算时间
      const complexity = task.metadata?.complexity || 5;
      if (complexity >= 8) {
        complexityCount.high++;
        totalHours += 8;
      } else if (complexity >= 5) {
        complexityCount.medium++;
        totalHours += 4;
      } else {
        complexityCount.low++;
        totalHours += 2;
      }
    }
    
    return {
      totalTasks: tasks.length,
      estimatedHours: totalHours,
      complexity: complexityCount
    };
  }
}